import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { doc, updateDoc } from 'firebase/firestore';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { firestore } from '../../services/firebase';
import type { AlignmentGapItem, AlignmentStrategyItem, Tender } from '../../types/tender';

interface StrategyAndGapFillerCardProps {
  tender: Tender;
  mode: 'readonly' | 'interactive';
  onJumpToStep7?: (() => void) | undefined;
}

export const isStrategyItemEmpty = (item: AlignmentStrategyItem): boolean => {
  const hasTitle = Boolean(item.title?.trim());
  const hasBody = Boolean(item.recommendation?.trim() || item.userNotes?.trim());
  return !hasTitle || !hasBody;
};

export const isGapItemEmpty = (item: AlignmentGapItem): boolean => {
  const hasGapTitle = Boolean(item.gap?.trim());
  const hasResolution = Boolean(item.suggestedMitigation?.trim() || item.userResolution?.trim());
  return !hasGapTitle || !hasResolution;
};

const StrategyUsageGuide: React.FC = () => {
  const theme = useTheme();
  const brickColor = theme.palette.bidlogiq?.brick || '#D05C09';

  return (
    <Box sx={{ mb: 2, pl: 1 }}>
      <Typography variant="body2" sx={{ fontSize: '1rem', mb: 1 }}>
        <strong>Usage Guide:</strong>
      </Typography>
      <List
        dense
        sx={{
          listStyleType: 'disc',
          pl: 2,
          '& .MuiListItem-root': { display: 'list-item', py: 0.2, px: 0, fontSize: '1rem' },
        }}
      >
        <ListItem>
          <strong>Global Strategy Pillars:</strong> Review or customize the core positioning and
          commercial directives extracted from the Capability Report. Active pillars apply across
          the entire proposal to maintain consistent narrative framing.
        </ListItem>
        <ListItem>
          <strong>Gap Resolutions (Overrides AI Pivot):</strong> Enter how you are addressing a
          flagged capability gap (e.g., naming a delivery partner or scoping a specialist
          workstream). Your custom resolution takes strict priority over default AI suggestions and
          section focusers.
        </ListItem>
        <ListItem>
          <strong>Ignore or Delete Resolved Gaps:</strong> If you update your Knowledge Base (e.g.,
          uploading a renewed insurance certificate) or do not want a gap mentioned, toggle it off
          or delete it so it is completely excluded from future draft generation and Red-Team
          review.
        </ListItem>
        <ListItem>
          <strong>Empty Items:</strong> Incomplete or empty items are outlined in{' '}
          <Box component="span" sx={{ color: brickColor, fontWeight: 'bold' }}>
            brick orange
          </Box>{' '}
          and are automatically excluded from generation until completed.
        </ListItem>
      </List>
    </Box>
  );
};

const StrategyAndGapFillerCard: React.FC<StrategyAndGapFillerCardProps> = ({
  tender,
  mode,
  onJumpToStep7,
}) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const isReadOnly = mode === 'readonly';
  const brickColor = theme.palette.bidlogiq?.brick || '#D05C09';

  // WCAG AA compliant palette for Commercial badge in both light and dark modes
  const commercialBadgeSx =
    theme.palette.mode === 'dark'
      ? {
          bgcolor: 'rgba(211, 153, 81, 0.16)',
          color: '#ffd599',
          borderColor: '#d39951',
        }
      : {
          bgcolor: 'rgba(129, 42, 22, 0.08)',
          color: '#812A16',
          borderColor: '#812A16',
        };

  const [strategyItems, setStrategyItems] = useState<AlignmentStrategyItem[]>(
    () => tender.alignmentAnalysis?.strategyItems || []
  );
  const [gapItems, setGapItems] = useState<AlignmentGapItem[]>(
    () => tender.alignmentAnalysis?.gapItems || []
  );
  const [expanded, setExpanded] = useState<boolean>(mode === 'interactive');

  const isInitialMount = useRef(true);
  const lastServerPayloadRef = useRef<string>(
    JSON.stringify({
      strategyItems: tender.alignmentAnalysis?.strategyItems || [],
      gapItems: tender.alignmentAnalysis?.gapItems || [],
    })
  );

  useEffect(() => {
    const serverStrategies = tender.alignmentAnalysis?.strategyItems || [];
    const serverGaps = tender.alignmentAnalysis?.gapItems || [];
    const nextPayloadStr = JSON.stringify({
      strategyItems: serverStrategies,
      gapItems: serverGaps,
    });

    if (nextPayloadStr === lastServerPayloadRef.current) {
      return;
    }

    lastServerPayloadRef.current = nextPayloadStr;
    setStrategyItems(serverStrategies);
    setGapItems(serverGaps);
  }, [tender.alignmentAnalysis?.strategyItems, tender.alignmentAnalysis?.gapItems]);

  useEffect(() => {
    if (isReadOnly) {
      return;
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!tender?.id) {
      return;
    }

    const currentPayloadStr = JSON.stringify({
      strategyItems,
      gapItems,
    });

    if (currentPayloadStr === lastServerPayloadRef.current) {
      return;
    }

    const timer = setTimeout(async () => {
      try {
        if (!tender.id) {
          return;
        }
        const sanitizedStrategies = JSON.parse(JSON.stringify(strategyItems));
        const sanitizedGaps = JSON.parse(JSON.stringify(gapItems));
        lastServerPayloadRef.current = JSON.stringify({
          strategyItems: sanitizedStrategies,
          gapItems: sanitizedGaps,
        });
        const tenderRef = doc(firestore, 'tenders', tender.id);
        await updateDoc(tenderRef, {
          'alignmentAnalysis.strategyItems': sanitizedStrategies,
          'alignmentAnalysis.gapItems': sanitizedGaps,
        });
        showSnackbar('Strategy & gap mitigations saved!', 'success');
      } catch (_err) {
        showSnackbar('Failed to save strategy & gap mitigations', 'error');
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [strategyItems, gapItems, isReadOnly, tender?.id, showSnackbar]);

  const activeStrategiesCount = strategyItems.filter(
    (item) => item.enabled !== false && !isStrategyItemEmpty(item)
  ).length;
  const activeGapsCount = gapItems.filter(
    (item) => item.enabled !== false && item.status !== 'dismissed' && !isGapItemEmpty(item)
  ).length;

  if (isReadOnly && strategyItems.length === 0 && gapItems.length === 0) {
    return null;
  }

  const handleToggleStrategy = (id: string) => {
    setStrategyItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item))
    );
  };

  const handleUpdateStrategyField = (
    id: string,
    field: 'title' | 'recommendation' | 'userNotes',
    value: string
  ) => {
    setStrategyItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleDeleteStrategy = (id: string) => {
    setStrategyItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleAddStrategy = () => {
    const newId = `strat_custom_${Date.now()}`;
    setStrategyItems((prev) => [
      ...prev,
      {
        id: newId,
        title: '',
        category: 'strategy',
        recommendation: '',
        userNotes: '',
        enabled: true,
      },
    ]);
  };

  const handleToggleGap = (id: string) => {
    setGapItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) {
          return item;
        }
        const nextEnabled = !item.enabled;
        return {
          ...item,
          enabled: nextEnabled,
          status: nextEnabled ? (item.userResolution?.trim() ? 'resolved' : 'open') : 'dismissed',
        };
      })
    );
  };

  const handleUpdateGapField = (
    id: string,
    field: 'gap' | 'impact' | 'userResolution',
    value: string
  ) => {
    setGapItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) {
          return item;
        }
        const updated = { ...item, [field]: value };
        if (field === 'userResolution') {
          updated.status = value.trim() ? 'resolved' : 'open';
          updated.enabled = true;
        }
        return updated;
      })
    );
  };

  const handleDeleteGap = (id: string) => {
    setGapItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleAddGap = () => {
    const newId = `gap_custom_${Date.now()}`;
    setGapItems((prev) => [
      ...prev,
      {
        id: newId,
        gap: '',
        impact: '',
        suggestedMitigation: '',
        userResolution: '',
        status: 'open',
        enabled: true,
      },
    ]);
  };

  return (
    <Box sx={{ mt: isReadOnly ? 3 : 0, mb: isReadOnly ? 1 : 0 }}>
      {!isReadOnly && <StrategyUsageGuide />}

      <Accordion
        expanded={expanded}
        onChange={(_, isExp) => setExpanded(isExp)}
        disableGutters
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
          borderRadius: '8px !important',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              pr: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LightbulbOutlinedIcon color="primary" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {isReadOnly
                    ? 'Strategic Alignment & Identified Gaps (Preview)'
                    : 'Interactive Strategy & Gap Filler'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isReadOnly
                    ? 'Quick-access summary of extracted Bid Strategy and Capability Gaps.'
                    : 'Govern global bid positioning, resolve or dismiss capability gaps, and maintain cross-section continuity.'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${activeStrategiesCount} Active Strateg${activeStrategiesCount === 1 ? 'y' : 'ies'}`}
              />
              <Chip
                size="small"
                color={activeGapsCount > 0 ? 'warning' : 'success'}
                variant="outlined"
                label={`${activeGapsCount} Active Gap${activeGapsCount === 1 ? '' : 's'}`}
              />
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 1, pb: 2.5 }}>
          {isReadOnly && (
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              action={
                onJumpToStep7 ? (
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={onJumpToStep7}
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap', alignSelf: 'center' }}
                  >
                    Manage in Step 7
                  </Button>
                ) : undefined
              }
              sx={{
                mb: 2.5,
                alignItems: 'center',
                '& .MuiAlert-action': { pt: 0, pl: 2 },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                Read-Only Preview
              </Typography>
              <List
                dense
                disablePadding
                sx={{
                  listStyleType: 'disc',
                  pl: 2.5,
                  '& .MuiListItem-root': {
                    display: 'list-item',
                    py: 0.1,
                    px: 0,
                    fontSize: '0.875rem',
                  },
                }}
              >
                <ListItem>Customize Bid &amp; Commercial Strategy pillars</ListItem>
                <ListItem>Resolve, ignore, or delete capability gaps</ListItem>
                <ListItem>
                  Edit section focusers in <strong>Step 7 (Strategy &amp; Structure)</strong>
                </ListItem>
              </List>
            </Alert>
          )}

          {/* Bid & Commercial Strategy Section */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Bid &amp; Commercial Strategy Pillars
              </Typography>
            </Box>

            {strategyItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No strategic directives extracted yet. Run Capability Analysis or add a custom
                directive below.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {strategyItems.map((item) => {
                  const isEnabled = item.enabled !== false;
                  const isEmpty = isStrategyItemEmpty(item);
                  const isCommercial = item.category?.toLowerCase() === 'commercial';
                  return (
                    <Paper
                      key={item.id}
                      data-testid={`strategy-card-${item.id}`}
                      data-empty={isEmpty ? 'true' : 'false'}
                      variant="outlined"
                      sx={{
                        p: 2,
                        opacity: isEnabled ? 1 : 0.55,
                        bgcolor: isEnabled
                          ? 'background.paper'
                          : alpha(theme.palette.action.disabledBackground, 0.3),
                        border: isEmpty
                          ? `2px solid ${brickColor}`
                          : `1px solid ${theme.palette.divider}`,
                        transition: 'opacity 0.2s ease, border-color 0.2s ease',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.75,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              size="small"
                              label={isCommercial ? 'Commercial' : 'Bid Strategy'}
                              color={isCommercial ? 'default' : 'primary'}
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                ...(isCommercial ? commercialBadgeSx : {}),
                              }}
                            />
                            {isEmpty && (
                              <Chip
                                size="small"
                                icon={
                                  <WarningAmberOutlinedIcon
                                    sx={{
                                      fontSize: '0.85rem !important',
                                      color: `${brickColor} !important`,
                                    }}
                                  />
                                }
                                label="Empty (Excluded from Generation)"
                                variant="outlined"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  color: brickColor,
                                  borderColor: brickColor,
                                }}
                              />
                            )}
                            {isReadOnly ? (
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {item.title}
                              </Typography>
                            ) : (
                              <TextField
                                variant="standard"
                                value={item.title}
                                onChange={(e) =>
                                  handleUpdateStrategyField(item.id, 'title', e.target.value)
                                }
                                placeholder="Enter strategy title (required)..."
                                fullWidth
                                slotProps={{
                                  input: {
                                    sx: { fontWeight: 700, fontSize: '0.9rem' },
                                  },
                                }}
                              />
                            )}
                          </Box>

                          {isReadOnly ? (
                            <Typography variant="body2" color="text.secondary">
                              {item.recommendation}
                            </Typography>
                          ) : (
                            <>
                              <TextField
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={1}
                                maxRows={4}
                                fullWidth
                                label="AI Strategy Recommendation"
                                placeholder="Describe the strategic or commercial directive..."
                                value={item.recommendation}
                                onChange={(e) =>
                                  handleUpdateStrategyField(
                                    item.id,
                                    'recommendation',
                                    e.target.value
                                  )
                                }
                                sx={{ mt: 1 }}
                              />
                              <TextField
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={1}
                                maxRows={3}
                                fullWidth
                                label="Your Custom Direction / Override (Optional)"
                                placeholder="Add specific framing or instructions to apply across the proposal..."
                                value={item.userNotes || ''}
                                onChange={(e) =>
                                  handleUpdateStrategyField(item.id, 'userNotes', e.target.value)
                                }
                                sx={{ mt: 1.25 }}
                              />
                            </>
                          )}

                          {isReadOnly && item.userNotes && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.75,
                                color: 'primary.main',
                                fontWeight: 600,
                              }}
                            >
                              User Direction: {item.userNotes}
                            </Typography>
                          )}
                        </Box>

                        {!isReadOnly && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip
                              title={
                                isEnabled
                                  ? 'Included in draft generation & review (click to ignore)'
                                  : 'Ignored in draft generation & review (click to re-enable)'
                              }
                            >
                              <Switch
                                size="small"
                                checked={isEnabled}
                                onChange={() => handleToggleStrategy(item.id)}
                                inputProps={{
                                  'aria-label': `Toggle strategy ${item.title || item.id}`,
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Remove from future generation context">
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Remove strategy ${item.title || item.id}`}
                                onClick={() => handleDeleteStrategy(item.id)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {!isReadOnly && (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddStrategy}
                >
                  Add Strategy Directive
                </Button>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Limitations & Gap Filler Section */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GppMaybeIcon color="warning" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Identified Capability Gaps &amp; Mitigations
                </Typography>
              </Box>
            </Box>

            {gapItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No capability gaps currently flagged.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {gapItems.map((item) => {
                  const isEnabled = item.enabled !== false && item.status !== 'dismissed';
                  const isEmpty = isGapItemEmpty(item);
                  const hasCustomResolution = Boolean(item.userResolution?.trim());
                  return (
                    <Paper
                      key={item.id}
                      data-testid={`gap-card-${item.id}`}
                      data-empty={isEmpty ? 'true' : 'false'}
                      variant="outlined"
                      sx={{
                        p: 2,
                        opacity: isEnabled ? 1 : 0.55,
                        bgcolor: isEnabled
                          ? 'background.paper'
                          : alpha(theme.palette.action.disabledBackground, 0.3),
                        border: isEmpty
                          ? `2px solid ${brickColor}`
                          : `1px solid ${theme.palette.divider}`,
                        borderLeft: isEmpty
                          ? `4px solid ${brickColor}`
                          : `4px solid ${
                              !isEnabled
                                ? theme.palette.divider
                                : hasCustomResolution
                                  ? theme.palette.success.main
                                  : theme.palette.warning.main
                            }`,
                        transition: 'opacity 0.2s ease, border-color 0.2s ease',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              size="small"
                              icon={
                                !isEnabled ? (
                                  <VisibilityOffOutlinedIcon
                                    sx={{ fontSize: '0.85rem !important' }}
                                  />
                                ) : isEmpty ? (
                                  <WarningAmberOutlinedIcon
                                    sx={{
                                      fontSize: '0.85rem !important',
                                      color: `${brickColor} !important`,
                                    }}
                                  />
                                ) : hasCustomResolution ? (
                                  <CheckCircleOutlineIcon sx={{ fontSize: '0.85rem !important' }} />
                                ) : undefined
                              }
                              label={
                                !isEnabled
                                  ? 'Ignored / Excluded'
                                  : isEmpty
                                    ? 'Empty (Excluded from Generation)'
                                    : hasCustomResolution
                                      ? 'Custom Resolution Active'
                                      : 'AI Mitigation Active'
                              }
                              color={
                                !isEnabled || isEmpty
                                  ? 'default'
                                  : hasCustomResolution
                                    ? 'success'
                                    : 'warning'
                              }
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                ...(isEmpty
                                  ? {
                                      color: brickColor,
                                      borderColor: brickColor,
                                    }
                                  : {}),
                              }}
                            />
                            {isReadOnly ? (
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {item.gap}
                              </Typography>
                            ) : (
                              <TextField
                                variant="standard"
                                value={item.gap}
                                onChange={(e) =>
                                  handleUpdateGapField(item.id, 'gap', e.target.value)
                                }
                                placeholder="Enter gap or caveat title (required)..."
                                fullWidth
                                slotProps={{
                                  input: {
                                    sx: { fontWeight: 700, fontSize: '0.9rem' },
                                  },
                                }}
                              />
                            )}
                          </Box>

                          {item.impact && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Impact:</strong> {item.impact}
                            </Typography>
                          )}

                          {item.suggestedMitigation && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: isReadOnly ? 0.5 : 1.25,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.warning.main, 0.06),
                              }}
                            >
                              <strong>AI Recommended Pivot:</strong> {item.suggestedMitigation}
                            </Typography>
                          )}

                          {!isReadOnly ? (
                            <TextField
                              variant="outlined"
                              size="small"
                              multiline
                              minRows={1}
                              maxRows={4}
                              fullWidth
                              label="How we are resolving or framing this gap (Overrides AI Pivot)"
                              placeholder="e.g., Partnering with PanelVendor X for quarterly consumer surveys, or leave blank to use the AI Recommended Pivot..."
                              value={item.userResolution || ''}
                              onChange={(e) =>
                                handleUpdateGapField(item.id, 'userResolution', e.target.value)
                              }
                              sx={{ mt: item.suggestedMitigation ? 0 : 1 }}
                            />
                          ) : (
                            hasCustomResolution && (
                              <Typography
                                variant="body2"
                                sx={{ color: 'success.main', fontWeight: 600, mt: 0.5 }}
                              >
                                Approved Resolution: {item.userResolution}
                              </Typography>
                            )
                          )}
                        </Box>

                        {!isReadOnly && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip
                              title={
                                isEnabled
                                  ? 'Ignore this gap (exclude from future draft generation & review)'
                                  : 'Restore this gap into generation context'
                              }
                            >
                              <IconButton
                                size="small"
                                color={isEnabled ? 'default' : 'primary'}
                                aria-label={
                                  isEnabled
                                    ? `Ignore gap ${item.gap || item.id}`
                                    : `Restore gap ${item.gap || item.id}`
                                }
                                onClick={() => handleToggleGap(item.id)}
                              >
                                {isEnabled ? (
                                  <VisibilityOffOutlinedIcon fontSize="small" />
                                ) : (
                                  <RestoreIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete gap permanently (e.g., resolved in Knowledge Base)">
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Delete gap ${item.gap || item.id}`}
                                onClick={() => handleDeleteGap(item.id)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {!isReadOnly && (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddGap}
                >
                  Add Gap / Caveat
                </Button>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default StrategyAndGapFillerCard;
